import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  confirmInboundSourceLocation,
  getInboundCustomerDetail,
  listInboundCustomers,
  openSavedInboundIdeaRoomAsset,
  saveInboundIdeaRoomAsset,
  type InboundCustomerDetailResponse,
  type InboundCustomerProfileSummary,
} from "@/lib/orderIntakeCustomersClient";
import { listOrgLocationsApi } from "@/lib/orgLocations/orgLocationsClient";
import type { OrgLocation } from "@/lib/orgLocations/orgLocationsTypes";
import type { IntakeTab } from "./useOrderIntakeReview";

const PAGE_SIZE = 10;

export function useIdeaRoomCustomerInbox(activeTab: IntakeTab, enabled: boolean) {
  const [profiles, setProfiles] = useState<InboundCustomerProfileSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [unresolvedIdentity, setUnresolvedIdentity] = useState(false);
  const [unresolvedLocation, setUnresolvedLocation] = useState(false);
  const [eventType, setEventType] = useState("");
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [detail, setDetail] = useState<InboundCustomerDetailResponse | null>(null);
  const [locations, setLocations] = useState<OrgLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [assetActionId, setAssetActionId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);

  const refreshList = useCallback(async () => {
    if (!enabled) return;
    const ticket = ++requestRef.current;
    setLoading(true);
    setError(null);
    const result = await listInboundCustomers({
      status: activeTab,
      search,
      unresolved_identity: unresolvedIdentity,
      unresolved_location: unresolvedLocation,
      event_type: eventType || undefined,
      source: "idearoom",
      limit: PAGE_SIZE,
      offset: pageIndex * PAGE_SIZE,
    });
    if (ticket !== requestRef.current) return;
    setLoading(false);
    setLoaded(true);
    if (!result.ok) {
      setProfiles([]);
      setTotal(0);
      setError(result.error);
      return;
    }
    setProfiles(result.data.profiles);
    setTotal(result.data.total);
  }, [
    activeTab,
    enabled,
    eventType,
    pageIndex,
    search,
    unresolvedIdentity,
    unresolvedLocation,
  ]);

  useEffect(() => {
    const task = window.setTimeout(() => void refreshList(), 0);
    return () => window.clearTimeout(task);
  }, [refreshList]);

  const resetFilterPage = () => {
    setPageIndex(0);
    setSelectedProfileId(null);
    setDetail(null);
  };

  const loadDetail = useCallback(async (profileId: string) => {
    setSelectedProfileId(profileId);
    setDetailLoading(true);
    setError(null);
    const [detailResult, locationResult] = await Promise.all([
      getInboundCustomerDetail(profileId),
      listOrgLocationsApi(),
    ]);
    setDetailLoading(false);
    if (!detailResult.ok) {
      setDetail(null);
      setError(detailResult.error);
      return;
    }
    setDetail(detailResult.data);
    if (locationResult.ok) {
      setLocations(locationResult.data.locations.filter((location) => location.is_active));
    }
  }, []);

  const toggleProfile = useCallback(
    (profileId: string) => {
      if (selectedProfileId === profileId) {
        setSelectedProfileId(null);
        setDetail(null);
        return;
      }
      void loadDetail(profileId);
    },
    [loadDetail, selectedProfileId]
  );

  const confirmLocation = useCallback(
    async (threadId: string, locationId: string): Promise<boolean> => {
      if (!selectedProfileId || !locationId) return false;
      setConfirming(true);
      setError(null);
      const result = await confirmInboundSourceLocation({
        order_thread_id: threadId,
        location_id: locationId,
      });
      setConfirming(false);
      if (!result.ok) {
        setError(result.error);
        return false;
      }
      await Promise.all([loadDetail(selectedProfileId), refreshList()]);
      return true;
    },
    [loadDetail, refreshList, selectedProfileId]
  );

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const actOnAsset = useCallback(async (candidateId: string, action: "save" | "retry" | "open") => {
    if (!selectedProfileId) return false;
    setAssetActionId(candidateId);
    setError(null);
    if (action === "open") {
      const result = await openSavedInboundIdeaRoomAsset(candidateId);
      setAssetActionId(null);
      if (!result.ok) {
        setError(result.error);
        return false;
      }
      window.open(result.data.signed_url, "_blank", "noopener,noreferrer");
    } else {
      const result = await saveInboundIdeaRoomAsset(candidateId, action === "retry");
      setAssetActionId(null);
      if (!result.ok) {
        setError(result.error);
        return false;
      }
    }
    await loadDetail(selectedProfileId);
    return true;
  }, [loadDetail, selectedProfileId]);
  const page = useMemo(
    () => ({
      pageIndex,
      pageCount,
      pageSize: PAGE_SIZE as 10,
      total,
      firstShown: total ? pageIndex * PAGE_SIZE + 1 : 0,
      lastShown: total ? Math.min(total, (pageIndex + 1) * PAGE_SIZE) : 0,
    }),
    [pageCount, pageIndex, total]
  );

  return {
    profiles,
    detail,
    locations,
    selectedProfileId,
    loading,
    detailLoading,
    confirming,
    assetActionId,
    loaded,
    error,
    page,
    searchDraft,
    setSearchDraft,
    submitSearch: () => {
      resetFilterPage();
      setSearch(searchDraft.trim());
    },
    unresolvedIdentity,
    setUnresolvedIdentity: (value: boolean) => {
      resetFilterPage();
      setUnresolvedIdentity(value);
    },
    unresolvedLocation,
    setUnresolvedLocation: (value: boolean) => {
      resetFilterPage();
      setUnresolvedLocation(value);
    },
    eventType,
    setEventType: (value: string) => {
      resetFilterPage();
      setEventType(value);
    },
    setPageIndex,
    toggleProfile,
    confirmLocation,
    actOnAsset,
    refresh: refreshList,
  };
}
